import React from "react";
import { Rnd } from "react-rnd";

import type {
  TFaceCensorshipDetectedRegion,
  TImageCoordinateFrameMetadata,
} from "../../../../services/api-image-tools";
import { APP_TOKENS as tokens } from "../../../../styles/tokens";
import { clampToRange } from "../../../../utils/censorshipGeometry";
import { getStickerUpscaleWarning } from "../../../../utils/stickerResolution";
import { SecondaryButton } from "../../../common/native";
import ImageFilterIcon, {
  type TImageFilterIconName,
} from "../../../common/web/ImageFilterIcon.web";
import useSelectedLayerKeyboardShortcuts from "../../../common/web/useSelectedLayerKeyboardShortcuts.web";
import { EditorActionButton } from "./CensorshipResultEditor.web";
import {
  createCensorshipStickerPlacements,
  type TCensorshipStickerPlacement,
} from "./censorshipStickerPlacements";
import {
  createManualFaceRegion,
  fitFaceRegionToBounds,
  getFaceRegionBounds,
  getLocalPolygonPoints,
  mapDetectedFaceRegionsToSource,
  removeFaceRegion,
  type TFaceBlurRegion,
  type TNormalizedBounds,
} from "./faceCensorshipGeometry";
import { getFaceCensorshipWarningCopy } from "./faceCensorshipWarnings";
import type {
  TImageFilterId,
  TImageToolsJobResult,
} from "./ImageToolsSections";
import {
  duplicateOverlayPlacement,
  removeOverlayPlacement,
} from "./overlayPlacementOperations";
import { IMAGE_TOOLS_TOKENS as imageToolsTokens } from "./tokens";
import {
  getAdjacentToolIndex,
  getEditorSelectionKey,
  getInitialReviewTool,
} from "./unifiedEditorState";

type TStageSize = { width: number; height: number };
type TImageSize = { width: number; height: number };
type TBbox = [number, number, number, number] | null;

const KEYBOARD_MOVE_RATIO = 0.01;
const KEYBOARD_FINE_MOVE_RATIO = 0.0025;
const MANUAL_STICKER_INITIAL_POSITION_RATIO = 0.35;
const MANUAL_STICKER_SIZE_RATIO = 0.3;
const MANUAL_STICKER_CASCADE_STEP_RATIO = 0.04;
const MANUAL_STICKER_MAX_CASCADE_OFFSET_RATIO = 0.25;

const TOOL_DEFINITIONS: Record<
  TImageFilterId,
  {
    label: string;
    shortLabel: string;
    icon: TImageFilterIconName;
  }
> = {
  "hide-faces": {
    label: "Face censorship",
    shortLabel: "Face",
    icon: "face",
  },
  "cover-privates": {
    label: "Private areas",
    shortLabel: "Private",
    icon: "privacy",
  },
  "blur-tattoos": {
    label: "Tattoo blur",
    shortLabel: "Tattoo",
    icon: "tattoo",
  },
  "remove-background": {
    label: "Background removal",
    shortLabel: "Background",
    icon: "remove-background",
  },
};

const getCanonicalCensorshipBboxes = (
  metadata: unknown,
): Record<string, [number, number, number, number]> => {
  if (!metadata || typeof metadata !== "object") return {};
  const regions = (metadata as { regions?: unknown }).regions;
  if (!Array.isArray(regions)) return {};

  return Object.fromEntries(
    regions.flatMap((region, index) => {
      if (!region || typeof region !== "object") return [];
      const candidate = region as {
        id?: unknown;
        label?: unknown;
        shape_type?: unknown;
        coord_space?: unknown;
        geometry?: Record<string, unknown>;
      };
      const values = [
        candidate.geometry?.x1,
        candidate.geometry?.y1,
        candidate.geometry?.x2,
        candidate.geometry?.y2,
      ].map(Number);
      if (
        candidate.shape_type !== "bbox" ||
        candidate.coord_space !== "processed_image_px" ||
        values.some((value) => !Number.isFinite(value))
      ) {
        return [];
      }
      const key =
        typeof candidate.label === "string"
          ? candidate.label
          : typeof candidate.id === "string"
            ? candidate.id
            : `private-area-${index + 1}`;
      return [[key, values as [number, number, number, number]]];
    }),
  );
};

const getPrivateBboxes = (result: TImageToolsJobResult, imageIndex: number) => {
  const image = result.metadata.images[imageIndex];
  return {
    ...getCanonicalCensorshipBboxes(image?.ai_filters?.censorship),
    ...(image?.final_bboxes_on_original || {}),
  } as Record<string, TBbox>;
};

const getFaceData = (result: TImageToolsJobResult, imageIndex: number) => {
  const metadataImage = result.metadata.images[imageIndex];
  const metadata = metadataImage?.ai_filters?.face_censorship;
  const detectedRegions = metadata?.regions || [];
  const coordinateFrame = metadataImage?.coordinate_frame;
  const mapping = mapDetectedFaceRegionsToSource({
    regions: detectedRegions,
    coordinateFrame,
  });
  const detectionError = Boolean(
    result.metadata.errors?.[metadataImage?.input_s3_key || ""] && !metadata,
  );
  return {
    metadata,
    detectedRegions,
    coordinateFrame,
    mapping,
    detectionError,
  };
};

const getFaceStickerBboxes = ({
  regions,
  coordinateFrame,
}: {
  regions: readonly TFaceBlurRegion[];
  coordinateFrame?: TImageCoordinateFrameMetadata | null;
}) => {
  if (!coordinateFrame) return {};
  return Object.fromEntries(
    regions.map((region) => {
      const bounds = getFaceRegionBounds(region);
      return [
        region.id,
        [
          bounds.x * coordinateFrame.source_width_px,
          bounds.y * coordinateFrame.source_height_px,
          (bounds.x + bounds.width) * coordinateFrame.source_width_px,
          (bounds.y + bounds.height) * coordinateFrame.source_height_px,
        ] as [number, number, number, number],
      ];
    }),
  );
};

const getAutomaticRegionCount = (
  result: TImageToolsJobResult,
  imageIndex: number,
  filterName: "tattoo" | "background",
) => {
  const metadata = result.metadata.images[imageIndex]?.ai_filters?.[
    filterName
  ] as
    | {
        regions?: unknown;
        detected_region_count?: unknown;
        region_count?: unknown;
      }
    | undefined;
  if (Array.isArray(metadata?.regions)) return metadata.regions.length;
  const suppliedCount = Number(
    metadata?.detected_region_count ?? metadata?.region_count,
  );
  return Number.isFinite(suppliedCount) ? suppliedCount : null;
};

const ToolTab: React.FC<{
  toolId: TImageFilterId;
  active: boolean;
  warning: boolean;
  count: number | null;
  mobile: boolean;
  tabRef: (element: HTMLButtonElement | null) => void;
  onSelect: () => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
}> = ({
  toolId,
  active,
  warning,
  count,
  mobile,
  tabRef,
  onSelect,
  onKeyDown,
}) => {
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [pressed, setPressed] = React.useState(false);
  const definition = TOOL_DEFINITIONS[toolId];
  const statusCopy = warning
    ? "Review required"
    : count === null
      ? "Applied"
      : `${count} ${count === 1 ? "item" : "items"}`;

  return (
    <button
      ref={tabRef}
      type="button"
      role="tab"
      id={`image-tools-tab-${toolId}`}
      aria-controls={`image-tools-panel-${toolId}`}
      aria-label={`${definition.label}. ${statusCopy}.`}
      aria-selected={active}
      data-testid={`image-tools-review-tool-${toolId}`}
      tabIndex={active ? 0 : -1}
      title={`${definition.label} — ${statusCopy}`}
      onClick={onSelect}
      onKeyDown={onKeyDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        setPressed(false);
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        setPressed(false);
      }}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      style={{
        ...workspaceStyles.toolTab,
        ...(mobile ? workspaceStyles.mobileToolTab : {}),
        ...(active ? workspaceStyles.activeToolTab : {}),
        ...(hovered ? workspaceStyles.hoveredControl : {}),
        ...(focused ? workspaceStyles.focusedControl : {}),
        ...(pressed ? workspaceStyles.pressedControl : {}),
      }}
    >
      <span style={workspaceStyles.toolIconWrap}>
        <ImageFilterIcon
          name={definition.icon}
          color={active ? tokens.color.accentDark : tokens.color.textPrimary}
          size={tokens.fontSize.xxl}
        />
        <span
          aria-hidden="true"
          style={{
            ...workspaceStyles.toolStatus,
            ...(warning ? workspaceStyles.warningStatus : {}),
          }}
        >
          {warning ? "!" : (count ?? "✓")}
        </span>
      </span>
      <span style={workspaceStyles.toolTabLabel}>{definition.shortLabel}</span>
    </button>
  );
};

const PhotoButton: React.FC<{
  index: number;
  imageCount: number;
  imageUrl: string;
  active: boolean;
  warning: boolean;
  onSelect: () => void;
}> = ({ index, imageCount, imageUrl, active, warning, onSelect }) => {
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [pressed, setPressed] = React.useState(false);

  return (
    <button
      type="button"
      aria-label={`Photo ${index + 1} of ${imageCount}${warning ? ", review required" : ""}`}
      aria-current={active ? "true" : undefined}
      data-testid={`image-tools-photo-${index}`}
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        setPressed(false);
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        setPressed(false);
      }}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      style={{
        ...workspaceStyles.photoButton,
        ...(active ? workspaceStyles.activePhotoButton : {}),
        ...(hovered ? workspaceStyles.hoveredControl : {}),
        ...(focused ? workspaceStyles.focusedControl : {}),
        ...(pressed ? workspaceStyles.pressedControl : {}),
      }}
    >
      <img
        src={imageUrl}
        alt=""
        aria-hidden="true"
        style={workspaceStyles.photoThumbnail}
      />
      <span style={workspaceStyles.photoNumber}>{index + 1}</span>
      {warning ? (
        <span aria-hidden="true" style={workspaceStyles.photoWarning}>
          !
        </span>
      ) : null}
    </button>
  );
};

const StickerOverlay: React.FC<{
  placement: TCensorshipStickerPlacement;
  placementIndex: number;
  imageIndex: number;
  stageSize: TStageSize;
  stickerUrl: string;
  selected: boolean;
  active: boolean;
  helpId: string;
  onSelect: (id: string) => void;
  onChange: (
    id: string,
    update: Partial<
      Pick<TCensorshipStickerPlacement, "x" | "y" | "width" | "height">
    >,
  ) => void;
  onStickerLoad: (size: TImageSize) => void;
}> = ({
  placement,
  placementIndex,
  imageIndex,
  stageSize,
  stickerUrl,
  selected,
  active,
  helpId,
  onSelect,
  onChange,
  onStickerLoad,
}) => (
  <Rnd
    bounds="parent"
    minWidth={imageToolsTokens.size.stickerMinSide}
    minHeight={imageToolsTokens.size.stickerMinSide}
    size={{
      width: placement.width * stageSize.width,
      height: placement.height * stageSize.height,
    }}
    position={{
      x: placement.x * stageSize.width,
      y: placement.y * stageSize.height,
    }}
    disableDragging={!active}
    enableResizing={active && selected}
    lockAspectRatio={false}
    style={{
      ...workspaceStyles.sticker,
      ...(active
        ? workspaceStyles.interactiveLayer
        : workspaceStyles.inactiveLayer),
      ...(selected && active ? workspaceStyles.selectedSticker : {}),
    }}
    onMouseDown={() => active && onSelect(placement.id)}
    onTouchStart={() => active && onSelect(placement.id)}
    onDragStart={() => active && onSelect(placement.id)}
    onDragStop={(_, data) =>
      onChange(placement.id, {
        x: data.x / stageSize.width,
        y: data.y / stageSize.height,
      })
    }
    onResizeStart={() => active && onSelect(placement.id)}
    onResizeStop={(_, __, ref, ___, position) =>
      onChange(placement.id, {
        x: position.x / stageSize.width,
        y: position.y / stageSize.height,
        width: ref.offsetWidth / stageSize.width,
        height: ref.offsetHeight / stageSize.height,
      })
    }
  >
    <div
      role="button"
      tabIndex={active ? 0 : -1}
      aria-label={`Sticker ${placementIndex + 1} for ${placement.regionKey}. Use arrow keys to move it, Command or Control plus C to duplicate it, or Delete to remove it.`}
      aria-describedby={helpId}
      aria-hidden={!active}
      data-testid={`censorship-sticker-${imageIndex}-${placement.id}`}
      onFocus={() => active && onSelect(placement.id)}
      onKeyDown={(event) => {
        if (!active) return;
        const step = event.shiftKey
          ? KEYBOARD_FINE_MOVE_RATIO
          : KEYBOARD_MOVE_RATIO;
        const nextPosition = { x: placement.x, y: placement.y };
        if (event.key === "ArrowLeft") nextPosition.x -= step;
        else if (event.key === "ArrowRight") nextPosition.x += step;
        else if (event.key === "ArrowUp") nextPosition.y -= step;
        else if (event.key === "ArrowDown") nextPosition.y += step;
        else return;
        event.preventDefault();
        onChange(placement.id, {
          x: clampToRange(nextPosition.x, 0, Math.max(0, 1 - placement.width)),
          y: clampToRange(nextPosition.y, 0, Math.max(0, 1 - placement.height)),
        });
      }}
      style={workspaceStyles.layerFocusTarget}
    >
      <img
        src={stickerUrl}
        alt=""
        aria-hidden="true"
        draggable={false}
        onLoad={(event) =>
          onStickerLoad({
            width: event.currentTarget.naturalWidth,
            height: event.currentTarget.naturalHeight,
          })
        }
        style={workspaceStyles.stickerImage}
      />
    </div>
  </Rnd>
);

const FaceBlurOverlay: React.FC<{
  region: TFaceBlurRegion;
  regionIndex: number;
  imageIndex: number;
  stageSize: TStageSize;
  selected: boolean;
  active: boolean;
  showDebugLabels: boolean;
  onSelect: (id: string) => void;
  onChange: (region: TFaceBlurRegion, bounds: TNormalizedBounds) => void;
}> = ({
  region,
  regionIndex,
  imageIndex,
  stageSize,
  selected,
  active,
  showDebugLabels,
  onSelect,
  onChange,
}) => {
  const bounds = getFaceRegionBounds(region);
  const polygonPoints = getLocalPolygonPoints(region);
  const clipPath = `polygon(${polygonPoints
    .split(" ")
    .map((point) => point.replace(",", "% ") + "%")
    .join(", ")})`;
  const originDescription = showDebugLabels
    ? `${region.origin === "detected" ? "Automatically detected" : "Manually added"} `
    : "";

  return (
    <Rnd
      bounds="parent"
      minWidth={imageToolsTokens.size.regionMinSide}
      minHeight={imageToolsTokens.size.regionMinSide}
      size={{
        width: bounds.width * stageSize.width,
        height: bounds.height * stageSize.height,
      }}
      position={{
        x: bounds.x * stageSize.width,
        y: bounds.y * stageSize.height,
      }}
      disableDragging={!active}
      enableResizing={active && selected}
      lockAspectRatio={false}
      style={{
        ...workspaceStyles.region,
        ...(active
          ? workspaceStyles.interactiveLayer
          : workspaceStyles.inactiveLayer),
      }}
      onMouseDown={() => active && onSelect(region.id)}
      onTouchStart={() => active && onSelect(region.id)}
      onDragStart={() => active && onSelect(region.id)}
      onDragStop={(_, data) =>
        onChange(region, {
          ...bounds,
          x: data.x / stageSize.width,
          y: data.y / stageSize.height,
        })
      }
      onResizeStart={() => active && onSelect(region.id)}
      onResizeStop={(_, __, ref, ___, position) =>
        onChange(region, {
          x: position.x / stageSize.width,
          y: position.y / stageSize.height,
          width: ref.offsetWidth / stageSize.width,
          height: ref.offsetHeight / stageSize.height,
        })
      }
    >
      <div
        role="button"
        tabIndex={active ? 0 : -1}
        aria-hidden={!active}
        aria-label={`${originDescription}face blur region ${regionIndex + 1}. Use arrow keys to move it or Delete to remove it.`}
        aria-describedby={`face-blur-editor-help-${imageIndex}`}
        data-testid={`face-blur-region-${imageIndex}-${region.id}`}
        data-region-origin={region.origin}
        onFocus={() => active && onSelect(region.id)}
        onKeyDown={(event) => {
          if (!active) return;
          const step = event.shiftKey
            ? KEYBOARD_FINE_MOVE_RATIO
            : KEYBOARD_MOVE_RATIO;
          const nextBounds = { ...bounds };
          if (event.key === "ArrowLeft") nextBounds.x -= step;
          else if (event.key === "ArrowRight") nextBounds.x += step;
          else if (event.key === "ArrowUp") nextBounds.y -= step;
          else if (event.key === "ArrowDown") nextBounds.y += step;
          else return;
          event.preventDefault();
          onChange(region, {
            ...nextBounds,
            x: clampToRange(nextBounds.x, 0, Math.max(0, 1 - nextBounds.width)),
            y: clampToRange(
              nextBounds.y,
              0,
              Math.max(0, 1 - nextBounds.height),
            ),
          });
        }}
        style={workspaceStyles.layerFocusTarget}
      >
        <div
          aria-hidden="true"
          style={{ ...workspaceStyles.blurPreview, clipPath }}
        />
        <svg
          aria-hidden="true"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={workspaceStyles.regionPolygon}
        >
          <polygon
            points={polygonPoints}
            fill={tokens.color.surfaceAccent}
            fillOpacity={imageToolsTokens.opacity.regionOverlay}
            stroke={
              selected && active ? tokens.color.accentDark : tokens.color.accent
            }
            strokeWidth={
              selected && active
                ? tokens.borderWidth.strong
                : tokens.borderWidth.thin
            }
            vectorEffect="non-scaling-stroke"
          />
        </svg>
        {showDebugLabels ? (
          <span style={workspaceStyles.regionBadge}>
            {region.origin === "detected" ? "Detected" : "Manual"}
            {region.fallbackUsed ? " · Fallback" : ""}
          </span>
        ) : null}
      </div>
    </Rnd>
  );
};

const InspectorStatus: React.FC<{
  warning: boolean;
  children: React.ReactNode;
}> = ({ warning, children }) => (
  <span
    style={{
      ...workspaceStyles.inspectorStatus,
      ...(warning ? workspaceStyles.inspectorWarningStatus : {}),
    }}
  >
    {warning ? "Review required" : children}
  </span>
);

const Notice: React.FC<{
  children: React.ReactNode;
  testID?: string;
  urgent?: boolean;
}> = ({ children, testID, urgent = false }) => (
  <div
    role={urgent ? "alert" : "status"}
    aria-live={urgent ? "assertive" : "polite"}
    data-testid={testID}
    style={urgent ? workspaceStyles.errorNotice : workspaceStyles.notice}
  >
    {children}
  </div>
);

const UnifiedImageToolsEditor: React.FC<{
  result: TImageToolsJobResult;
  imagePreviewUrls: string[];
  placementsByImage: Record<number, TCensorshipStickerPlacement[]>;
  faceStickerPlacementsByImage: Record<number, TCensorshipStickerPlacement[]>;
  faceBlurRegionsByImage: Record<number, TFaceBlurRegion[]>;
  downloadingIndex: number | null;
  isDownloadingAll: boolean;
  isHighQualityExporting: boolean;
  editorsReady: boolean;
  viewportWidth: number;
  showRegionDebugLabels: boolean;
  onDownload: (index: number) => void;
  onDownloadAll: () => void;
  onPlacementsChange: (
    index: number,
    placements: TCensorshipStickerPlacement[],
    isEdited: boolean,
  ) => void;
  onFaceStickerPlacementsChange: (
    index: number,
    placements: TCensorshipStickerPlacement[],
    isEdited: boolean,
  ) => void;
  onFaceBlurRegionsChange: (
    index: number,
    regions: TFaceBlurRegion[],
    isEdited: boolean,
  ) => void;
}> = ({
  result,
  imagePreviewUrls,
  placementsByImage,
  faceStickerPlacementsByImage,
  faceBlurRegionsByImage,
  downloadingIndex,
  isDownloadingAll,
  isHighQualityExporting,
  editorsReady,
  viewportWidth,
  showRegionDebugLabels,
  onDownload,
  onDownloadAll,
  onPlacementsChange,
  onFaceStickerPlacementsChange,
  onFaceBlurRegionsChange,
}) => {
  const isMobile = viewportWidth < imageToolsTokens.size.mobileBreakpoint;
  const isTablet = viewportWidth < imageToolsTokens.size.tabletBreakpoint;
  const editableToolIds = React.useMemo(
    () =>
      new Set<TImageFilterId>([
        ...(result.appliedFilters.includes("cover-privates") &&
        result.coverImageUrl
          ? (["cover-privates"] as const)
          : []),
        ...(result.appliedFilters.includes("hide-faces")
          ? (["hide-faces"] as const)
          : []),
      ]),
    [result.appliedFilters, result.coverImageUrl],
  );
  const getWarningToolIds = React.useCallback(
    (imageIndex: number) => {
      const warningIds = new Set<TImageFilterId>();
      if (result.appliedFilters.includes("hide-faces")) {
        const faceData = getFaceData(result, imageIndex);
        const approvedContent =
          result.faceCensorshipMode === "blur"
            ? faceBlurRegionsByImage[imageIndex]
            : faceStickerPlacementsByImage[imageIndex];
        const hasNoApprovedContent =
          approvedContent !== undefined
            ? approvedContent.length === 0
            : faceData.mapping.regions.length === 0;
        if (
          faceData.detectionError ||
          faceData.mapping.error ||
          Boolean(faceData.metadata?.warnings?.length) ||
          hasNoApprovedContent
        ) {
          warningIds.add("hide-faces");
        }
      }
      return warningIds;
    },
    [faceBlurRegionsByImage, faceStickerPlacementsByImage, result],
  );
  const [activeImageIndex, setActiveImageIndex] = React.useState(0);
  const [activeToolId, setActiveToolId] = React.useState<TImageFilterId>(
    () =>
      getInitialReviewTool({
        appliedFilters: result.appliedFilters,
        warningToolIds: getWarningToolIds(0),
        editableToolIds,
      }) || result.appliedFilters[0],
  );
  const [selectedLayerByImageAndTool, setSelectedLayerByImageAndTool] =
    React.useState<Record<string, string | null>>({});
  const [imageSizes, setImageSizes] = React.useState<
    Record<number, TImageSize>
  >({});
  const [stickerSize, setStickerSize] = React.useState<TImageSize>({
    width: 0,
    height: 0,
  });
  const [stageSize, setStageSize] = React.useState<TStageSize>({
    width: 0,
    height: 0,
  });
  const stageContainerRef = React.useRef<HTMLDivElement | null>(null);
  const toolRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

  React.useEffect(() => {
    setActiveImageIndex(0);
    setSelectedLayerByImageAndTool({});
    setActiveToolId(
      getInitialReviewTool({
        appliedFilters: result.appliedFilters,
        warningToolIds: getWarningToolIds(0),
        editableToolIds,
      }) || result.appliedFilters[0],
    );
  }, [result.batchId]);

  React.useEffect(() => {
    imagePreviewUrls.forEach((url, index) => {
      if (!url || imageSizes[index]) return;
      const image = new window.Image();
      image.onload = () => {
        setImageSizes((current) =>
          current[index]
            ? current
            : {
                ...current,
                [index]: {
                  width: image.naturalWidth,
                  height: image.naturalHeight,
                },
              },
        );
      };
      image.src = url;
    });
  }, [imagePreviewUrls, imageSizes]);

  React.useEffect(() => {
    result.downloadUrls.forEach((_, imageIndex) => {
      const initializeSelection = (
        toolId: TImageFilterId,
        layerId: string | null,
      ) => {
        const key = getEditorSelectionKey(imageIndex, toolId);
        setSelectedLayerByImageAndTool((current) =>
          Object.prototype.hasOwnProperty.call(current, key)
            ? current
            : { ...current, [key]: layerId },
        );
      };
      const imageSize = imageSizes[imageIndex];
      if (
        result.appliedFilters.includes("cover-privates") &&
        result.coverImageUrl &&
        imageSize &&
        placementsByImage[imageIndex] === undefined
      ) {
        const initialPlacements = createCensorshipStickerPlacements({
          bboxes: getPrivateBboxes(result, imageIndex),
          imageWidth: imageSize.width,
          imageHeight: imageSize.height,
        });
        onPlacementsChange(imageIndex, initialPlacements, false);
        initializeSelection("cover-privates", initialPlacements[0]?.id ?? null);
      }

      if (!result.appliedFilters.includes("hide-faces")) return;
      const faceData = getFaceData(result, imageIndex);
      if (
        result.faceCensorshipMode === "blur" &&
        faceBlurRegionsByImage[imageIndex] === undefined &&
        !faceData.mapping.error
      ) {
        onFaceBlurRegionsChange(imageIndex, faceData.mapping.regions, false);
        initializeSelection(
          "hide-faces",
          faceData.mapping.regions[0]?.id ?? null,
        );
      }
      if (
        result.faceCensorshipMode === "sticker" &&
        faceStickerPlacementsByImage[imageIndex] === undefined &&
        !faceData.mapping.error
      ) {
        if (!faceData.mapping.regions.length) {
          onFaceStickerPlacementsChange(imageIndex, [], false);
          initializeSelection("hide-faces", null);
        } else if (faceData.coordinateFrame) {
          const initialPlacements = createCensorshipStickerPlacements({
            bboxes: getFaceStickerBboxes({
              regions: faceData.mapping.regions,
              coordinateFrame: faceData.coordinateFrame,
            }),
            imageWidth: faceData.coordinateFrame.source_width_px,
            imageHeight: faceData.coordinateFrame.source_height_px,
          });
          onFaceStickerPlacementsChange(imageIndex, initialPlacements, false);
          initializeSelection("hide-faces", initialPlacements[0]?.id ?? null);
        }
      }
    });
  }, [
    faceBlurRegionsByImage,
    faceStickerPlacementsByImage,
    imageSizes,
    onFaceBlurRegionsChange,
    onFaceStickerPlacementsChange,
    onPlacementsChange,
    placementsByImage,
    result,
  ]);

  const imageSize = imageSizes[activeImageIndex];
  React.useEffect(() => {
    const container = stageContainerRef.current;
    if (!container || !imageSize?.width || !imageSize.height) return;
    const updateStageSize = () => {
      const availableWidth = Math.max(
        0,
        container.clientWidth - tokens.spacing.lg * 2,
      );
      if (!availableWidth) return;
      const aspectRatio = imageSize.width / imageSize.height;
      const width = Math.min(
        availableWidth,
        imageToolsTokens.size.editorMaxHeight * aspectRatio,
      );
      setStageSize({ width, height: width / aspectRatio });
    };
    updateStageSize();
    const observer = new ResizeObserver(updateStageSize);
    observer.observe(container);
    return () => observer.disconnect();
  }, [activeImageIndex, imageSize]);

  const selectionKey = getEditorSelectionKey(activeImageIndex, activeToolId);
  const selectedLayerId = selectedLayerByImageAndTool[selectionKey] ?? null;
  const selectLayer = React.useCallback(
    (id: string | null) =>
      setSelectedLayerByImageAndTool((current) => ({
        ...current,
        [getEditorSelectionKey(activeImageIndex, activeToolId)]: id,
      })),
    [activeImageIndex, activeToolId],
  );

  const initialStickerPlacements = React.useCallback(
    (toolId: "cover-privates" | "hide-faces", imageIndex: number) => {
      if (toolId === "cover-privates") {
        const size = imageSizes[imageIndex];
        return size
          ? createCensorshipStickerPlacements({
              bboxes: getPrivateBboxes(result, imageIndex),
              imageWidth: size.width,
              imageHeight: size.height,
            })
          : [];
      }
      const faceData = getFaceData(result, imageIndex);
      return faceData.coordinateFrame && !faceData.mapping.error
        ? createCensorshipStickerPlacements({
            bboxes: getFaceStickerBboxes({
              regions: faceData.mapping.regions,
              coordinateFrame: faceData.coordinateFrame,
            }),
            imageWidth: faceData.coordinateFrame.source_width_px,
            imageHeight: faceData.coordinateFrame.source_height_px,
          })
        : [];
    },
    [imageSizes, result],
  );

  const commitStickerPlacements = React.useCallback(
    (
      toolId: "cover-privates" | "hide-faces",
      placements: TCensorshipStickerPlacement[],
    ) => {
      const initial = initialStickerPlacements(toolId, activeImageIndex);
      const isEdited = JSON.stringify(placements) !== JSON.stringify(initial);
      if (toolId === "cover-privates") {
        onPlacementsChange(activeImageIndex, placements, isEdited);
      } else {
        onFaceStickerPlacementsChange(activeImageIndex, placements, isEdited);
      }
    },
    [
      activeImageIndex,
      initialStickerPlacements,
      onFaceStickerPlacementsChange,
      onPlacementsChange,
    ],
  );

  const activeStickerPlacements =
    activeToolId === "cover-privates"
      ? placementsByImage[activeImageIndex]
      : faceStickerPlacementsByImage[activeImageIndex];

  const updateSticker = React.useCallback(
    (
      id: string,
      update: Partial<
        Pick<TCensorshipStickerPlacement, "x" | "y" | "width" | "height">
      >,
    ) => {
      if (
        (activeToolId !== "cover-privates" && activeToolId !== "hide-faces") ||
        !activeStickerPlacements
      ) {
        return;
      }
      commitStickerPlacements(
        activeToolId,
        activeStickerPlacements.map((placement) =>
          placement.id === id ? { ...placement, ...update } : placement,
        ),
      );
    },
    [activeStickerPlacements, activeToolId, commitStickerPlacements],
  );

  const deleteSelectedLayer = React.useCallback(
    (id: string) => {
      if (activeToolId === "cover-privates" || activeToolId === "hide-faces") {
        if (
          result.faceCensorshipMode !== "blur" ||
          activeToolId === "cover-privates"
        ) {
          if (!activeStickerPlacements) return;
          const nextPlacements = removeOverlayPlacement(
            activeStickerPlacements,
            id,
          );
          commitStickerPlacements(activeToolId, nextPlacements);
          selectLayer(nextPlacements[0]?.id ?? null);
          return;
        }
      }
      if (
        activeToolId === "hide-faces" &&
        result.faceCensorshipMode === "blur"
      ) {
        const regions = faceBlurRegionsByImage[activeImageIndex];
        if (!regions) return;
        const initial = getFaceData(result, activeImageIndex).mapping.regions;
        const nextRegions = removeFaceRegion(regions, id);
        onFaceBlurRegionsChange(
          activeImageIndex,
          nextRegions,
          JSON.stringify(nextRegions) !== JSON.stringify(initial),
        );
        selectLayer(nextRegions[0]?.id ?? null);
      }
    },
    [
      activeImageIndex,
      activeStickerPlacements,
      activeToolId,
      commitStickerPlacements,
      faceBlurRegionsByImage,
      onFaceBlurRegionsChange,
      result,
      selectLayer,
    ],
  );

  const duplicateSelectedLayer = React.useCallback(
    (id: string) => {
      if (
        (activeToolId !== "cover-privates" && activeToolId !== "hide-faces") ||
        (activeToolId === "hide-faces" &&
          result.faceCensorshipMode === "blur") ||
        !activeStickerPlacements ||
        !stageSize.width ||
        !stageSize.height
      ) {
        return;
      }
      const duplicate = duplicateOverlayPlacement({
        placements: activeStickerPlacements,
        sourceId: id,
        offset: {
          x: tokens.spacing.md / stageSize.width,
          y: tokens.spacing.md / stageSize.height,
        },
      });
      if (!duplicate.duplicatedPlacement) return;
      commitStickerPlacements(activeToolId, duplicate.placements);
      selectLayer(duplicate.duplicatedPlacement.id);
    },
    [
      activeStickerPlacements,
      activeToolId,
      commitStickerPlacements,
      result.faceCensorshipMode,
      selectLayer,
      stageSize,
    ],
  );

  const activeToolSupportsDuplicate =
    activeToolId === "cover-privates" ||
    (activeToolId === "hide-faces" && result.faceCensorshipMode === "sticker");
  useSelectedLayerKeyboardShortcuts({
    selectedLayerId: editableToolIds.has(activeToolId) ? selectedLayerId : null,
    onDelete: deleteSelectedLayer,
    onDuplicate: activeToolSupportsDuplicate
      ? duplicateSelectedLayer
      : undefined,
  });

  React.useEffect(() => {
    if (!selectedLayerId) return;
    const exists =
      activeToolId === "cover-privates" ||
      (activeToolId === "hide-faces" && result.faceCensorshipMode === "sticker")
        ? Boolean(
            activeStickerPlacements?.some(({ id }) => id === selectedLayerId),
          )
        : activeToolId === "hide-faces"
          ? Boolean(
              faceBlurRegionsByImage[activeImageIndex]?.some(
                ({ id }) => id === selectedLayerId,
              ),
            )
          : false;
    if (!exists) selectLayer(null);
  }, [
    activeImageIndex,
    activeStickerPlacements,
    activeToolId,
    faceBlurRegionsByImage,
    result.faceCensorshipMode,
    selectLayer,
    selectedLayerId,
  ]);

  const addFaceSticker = () => {
    const current = faceStickerPlacementsByImage[activeImageIndex] || [];
    const existingIds = new Set(current.map(({ id }) => id));
    let suffix = 1;
    while (existingIds.has(`manual-face-${suffix}`)) suffix += 1;
    const offset = Math.min(
      current.filter(({ regionKey }) => regionKey === "manual face").length *
        MANUAL_STICKER_CASCADE_STEP_RATIO,
      MANUAL_STICKER_MAX_CASCADE_OFFSET_RATIO,
    );
    const placement: TCensorshipStickerPlacement = {
      id: `manual-face-${suffix}`,
      regionKey: "manual face",
      x: MANUAL_STICKER_INITIAL_POSITION_RATIO + offset,
      y: MANUAL_STICKER_INITIAL_POSITION_RATIO + offset,
      width: MANUAL_STICKER_SIZE_RATIO,
      height: MANUAL_STICKER_SIZE_RATIO,
    };
    commitStickerPlacements("hide-faces", [...current, placement]);
    selectLayer(placement.id);
  };

  const updateFaceRegion = React.useCallback(
    (region: TFaceBlurRegion, bounds: TNormalizedBounds) => {
      const regions = faceBlurRegionsByImage[activeImageIndex];
      if (!regions) return;
      const initial = getFaceData(result, activeImageIndex).mapping.regions;
      const nextRegions = regions.map((candidate) =>
        candidate.id === region.id
          ? fitFaceRegionToBounds(region, bounds)
          : candidate,
      );
      onFaceBlurRegionsChange(
        activeImageIndex,
        nextRegions,
        JSON.stringify(nextRegions) !== JSON.stringify(initial),
      );
    },
    [activeImageIndex, faceBlurRegionsByImage, onFaceBlurRegionsChange, result],
  );

  const faceData = getFaceData(result, activeImageIndex);
  const privatePlacements = placementsByImage[activeImageIndex] || [];
  const faceStickerPlacements =
    faceStickerPlacementsByImage[activeImageIndex] || [];
  const faceBlurRegions = faceBlurRegionsByImage[activeImageIndex] || [];
  const warningToolIds = getWarningToolIds(activeImageIndex);
  const hasEditableTools = editableToolIds.size > 0;
  const baseImageUrl = hasEditableTools
    ? imagePreviewUrls[activeImageIndex]
    : result.downloadUrls[activeImageIndex];
  const processedPreviewVisible = !hasEditableTools;
  const privateInitial = initialStickerPlacements(
    "cover-privates",
    activeImageIndex,
  );
  const faceStickerInitial = initialStickerPlacements(
    "hide-faces",
    activeImageIndex,
  );
  const faceRegionsInitial = faceData.mapping.regions;
  const currentStickerPlacements =
    activeToolId === "cover-privates"
      ? privatePlacements
      : faceStickerPlacements;
  const selectedSticker = currentStickerPlacements.find(
    ({ id }) => id === selectedLayerId,
  );
  const selectedFaceRegion = faceBlurRegions.find(
    ({ id }) => id === selectedLayerId,
  );
  const stickerWarning = getStickerUpscaleWarning({
    intrinsicWidth: stickerSize.width,
    intrinsicHeight: stickerSize.height,
    imageWidth: imageSize?.width || 0,
    imageHeight: imageSize?.height || 0,
    placements: currentStickerPlacements,
  });
  const currentPhotoDownloadDisabled =
    downloadingIndex !== null ||
    isDownloadingAll ||
    isHighQualityExporting ||
    (result.appliedFilters.includes("cover-privates") &&
      placementsByImage[activeImageIndex] === undefined) ||
    (result.appliedFilters.includes("hide-faces") &&
      (result.faceCensorshipMode === "blur"
        ? faceBlurRegions.length === 0
        : faceStickerPlacements.length === 0));

  const selectTool = (toolId: TImageFilterId) => setActiveToolId(toolId);
  const handleToolKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    currentIndex: number,
  ) => {
    const nextIndex = getAdjacentToolIndex({
      currentIndex,
      key: event.key,
      toolCount: result.appliedFilters.length,
    });
    if (nextIndex === currentIndex) return;
    event.preventDefault();
    const nextTool = result.appliedFilters[nextIndex];
    setActiveToolId(nextTool);
    toolRefs.current[nextIndex]?.focus();
  };

  const renderStickerInspector = (toolId: "cover-privates" | "hide-faces") => {
    const isFace = toolId === "hide-faces";
    const placements = isFace ? faceStickerPlacements : privatePlacements;
    const initial = isFace ? faceStickerInitial : privateInitial;
    const selectionCopy = selectedSticker
      ? `${selectedSticker.regionKey} sticker selected`
      : placements.length
        ? `Select a ${isFace ? "face sticker" : "private cover"} to edit it.`
        : isFace
          ? "No face stickers placed. Add one manually."
          : initial.length
            ? "All detected stickers removed"
            : "No private areas detected.";
    return (
      <>
        <div style={workspaceStyles.modeRow}>
          <span style={workspaceStyles.modeLabel}>Mode</span>
          <strong style={workspaceStyles.modeValue}>Sticker</strong>
        </div>
        {result.coverImageUrl ? (
          <div style={workspaceStyles.stickerPreviewRow}>
            <span style={workspaceStyles.stickerPreviewBox}>
              <img
                src={result.coverImageUrl}
                alt={`${result.coverImageLabel || "Selected"} sticker preview`}
                style={workspaceStyles.stickerPreviewImage}
              />
            </span>
            <span style={workspaceStyles.stickerCopy}>
              <strong style={workspaceStyles.inspectorStrong}>
                {result.coverImageLabel ||
                  (result.coverAssetId ? "Clinic sticker" : "Selected sticker")}
              </strong>
              <span style={workspaceStyles.inspectorMuted}>
                {placements.length} {isFace ? "face sticker" : "private cover"}
                {placements.length === 1 ? "" : "s"}
              </span>
            </span>
          </div>
        ) : null}
        {stickerWarning && result.coverAssetId !== undefined ? (
          <Notice
            testID={`censorship-sticker-resolution-warning-${activeImageIndex}`}
          >
            <strong style={workspaceStyles.inspectorStrong}>
              Custom sticker resolution: {stickerWarning.intrinsicWidth} ×{" "}
              {stickerWarning.intrinsicHeight} px.
            </strong>{" "}
            This photo uses it up to {stickerWarning.maximumDestinationWidth} ×{" "}
            {stickerWarning.maximumDestinationHeight} px, so the downloaded
            sticker may look less sharp. Use a larger source image or reduce the
            sticker size.
          </Notice>
        ) : null}
        <p aria-live="polite" style={workspaceStyles.selectionCopy}>
          {selectionCopy}
        </p>
        <div style={workspaceStyles.inspectorActions}>
          {isFace ? (
            <EditorActionButton
              testID={`add-sticker-${activeImageIndex}`}
              onPress={addFaceSticker}
            >
              <ImageFilterIcon
                name="add"
                color={tokens.color.textPrimary}
                size={tokens.fontSize.xl}
              />
              Add Face Sticker
            </EditorActionButton>
          ) : null}
          <EditorActionButton
            disabled={!selectedSticker}
            testID={`duplicate-selected-sticker-${activeImageIndex}`}
            onPress={() =>
              selectedSticker && duplicateSelectedLayer(selectedSticker.id)
            }
          >
            <ImageFilterIcon
              name="duplicate"
              color={tokens.color.textPrimary}
              size={tokens.fontSize.xl}
            />
            {isFace ? "Duplicate Selected Sticker" : "Duplicate Selected Cover"}
          </EditorActionButton>
          <EditorActionButton
            disabled={!selectedSticker}
            danger
            testID={`delete-selected-sticker-${activeImageIndex}`}
            onPress={() =>
              selectedSticker && deleteSelectedLayer(selectedSticker.id)
            }
          >
            <ImageFilterIcon
              name="delete"
              color={tokens.color.errorText}
              size={tokens.fontSize.xl}
            />
            {isFace ? "Delete Selected Sticker" : "Delete Selected Cover"}
          </EditorActionButton>
          <EditorActionButton
            disabled={JSON.stringify(placements) === JSON.stringify(initial)}
            testID={`reset-stickers-${activeImageIndex}`}
            onPress={() => {
              commitStickerPlacements(toolId, initial);
              selectLayer(initial[0]?.id ?? null);
            }}
          >
            {isFace ? "Reset Face Stickers" : "Reset Private Covers"}
          </EditorActionButton>
        </div>
      </>
    );
  };

  const renderFaceBlurInspector = () => {
    const selectionCopy = selectedFaceRegion
      ? showRegionDebugLabels
        ? `${selectedFaceRegion.origin === "detected" ? "Detected" : "Manual"} blur region selected`
        : "Blur region selected"
      : faceBlurRegions.length
        ? "Select a blur region to edit it."
        : "No face regions detected. Add a region before export.";
    return (
      <>
        {faceData.detectionError ? (
          <Notice urgent testID={`face-detection-error-${activeImageIndex}`}>
            Automatic face detection failed. Add a blur region manually or
            switch to Sticker mode before exporting.
          </Notice>
        ) : null}
        {faceData.mapping.error ? (
          <Notice
            urgent
            testID={`face-coordinate-frame-error-${activeImageIndex}`}
          >
            {faceData.mapping.error}
          </Notice>
        ) : null}
        {(faceData.metadata?.warnings || []).map((warning) => (
          <Notice
            key={warning}
            testID={`face-warning-${activeImageIndex}-${warning}`}
          >
            {getFaceCensorshipWarningCopy(warning)}
          </Notice>
        ))}
        <div style={workspaceStyles.modeRow}>
          <span style={workspaceStyles.modeLabel}>Mode</span>
          <strong style={workspaceStyles.modeValue}>Blur</strong>
        </div>
        <p style={workspaceStyles.inspectorMuted}>
          {faceBlurRegions.length} approved region
          {faceBlurRegions.length === 1 ? "" : "s"}
        </p>
        <p aria-live="polite" style={workspaceStyles.selectionCopy}>
          {selectionCopy}
        </p>
        <div style={workspaceStyles.inspectorActions}>
          <EditorActionButton
            testID={`add-face-region-${activeImageIndex}`}
            disabled={Boolean(faceData.mapping.error)}
            onPress={() => {
              const nextRegion = createManualFaceRegion(faceBlurRegions);
              onFaceBlurRegionsChange(
                activeImageIndex,
                [...faceBlurRegions, nextRegion],
                true,
              );
              selectLayer(nextRegion.id);
            }}
          >
            <ImageFilterIcon
              name="add"
              color={tokens.color.textPrimary}
              size={tokens.fontSize.xl}
            />
            Add Blur Region
          </EditorActionButton>
          <EditorActionButton
            disabled={!selectedFaceRegion}
            danger
            testID={`delete-face-region-${activeImageIndex}`}
            onPress={() =>
              selectedFaceRegion && deleteSelectedLayer(selectedFaceRegion.id)
            }
          >
            <ImageFilterIcon
              name="delete"
              color={tokens.color.errorText}
              size={tokens.fontSize.xl}
            />
            Remove Selected Region
          </EditorActionButton>
          <EditorActionButton
            disabled={
              Boolean(faceData.mapping.error) ||
              JSON.stringify(faceBlurRegions) ===
                JSON.stringify(faceRegionsInitial)
            }
            testID={`restore-face-regions-${activeImageIndex}`}
            onPress={() => {
              onFaceBlurRegionsChange(
                activeImageIndex,
                faceRegionsInitial,
                false,
              );
              selectLayer(faceRegionsInitial[0]?.id ?? null);
            }}
          >
            Restore Detected Regions
          </EditorActionButton>
        </div>
      </>
    );
  };

  const renderInspectorContent = () => {
    if (activeToolId === "cover-privates") {
      return renderStickerInspector("cover-privates");
    }
    if (activeToolId === "hide-faces") {
      return result.faceCensorshipMode === "blur"
        ? renderFaceBlurInspector()
        : renderStickerInspector("hide-faces");
    }
    const count =
      activeToolId === "blur-tattoos"
        ? getAutomaticRegionCount(result, activeImageIndex, "tattoo")
        : null;
    return (
      <>
        <div style={workspaceStyles.appliedPanel}>
          <span aria-hidden="true" style={workspaceStyles.appliedMark}>
            ✓
          </span>
          <span>
            <strong style={workspaceStyles.inspectorStrong}>
              Applied automatically
            </strong>
            {count !== null ? (
              <span style={workspaceStyles.inspectorMuted}>
                {count} detected region{count === 1 ? "" : "s"}
              </span>
            ) : null}
          </span>
        </div>
        {!processedPreviewVisible ? (
          <Notice>
            Applied automatically. The final effect is rendered at source
            resolution during export.
          </Notice>
        ) : (
          <p style={workspaceStyles.inspectorMuted}>
            The processed effect is visible in this preview.
          </p>
        )}
      </>
    );
  };

  const summaryParts: string[] = [];
  if (result.appliedFilters.includes("hide-faces")) {
    const count =
      result.faceCensorshipMode === "blur"
        ? faceBlurRegions.length
        : faceStickerPlacements.length;
    summaryParts.push(
      `${count} face ${result.faceCensorshipMode === "blur" ? "region" : "sticker"}${count === 1 ? "" : "s"}`,
    );
  }
  if (result.appliedFilters.includes("cover-privates")) {
    summaryParts.push(
      `${privatePlacements.length} private cover${privatePlacements.length === 1 ? "" : "s"}`,
    );
  }
  if (result.appliedFilters.includes("blur-tattoos")) {
    summaryParts.push("tattoo blur applied");
  }
  if (result.appliedFilters.includes("remove-background")) {
    summaryParts.push("background removed");
  }

  const photoNavigator =
    result.downloadUrls.length > 1 ? (
      <div
        aria-label="Photos"
        style={{
          ...workspaceStyles.photoNavigator,
          ...(isTablet ? workspaceStyles.horizontalPhotoNavigator : {}),
        }}
      >
        {result.downloadUrls.map((_, index) => {
          const active = index === activeImageIndex;
          const warning = getWarningToolIds(index).size > 0;
          return (
            <PhotoButton
              key={`${result.batchId}-photo-${index}`}
              index={index}
              imageCount={result.downloadUrls.length}
              imageUrl={imagePreviewUrls[index] || result.downloadUrls[index]}
              active={active}
              warning={warning}
              onSelect={() => setActiveImageIndex(index)}
            />
          );
        })}
      </div>
    ) : null;

  const toolRail = (
    <div
      role="tablist"
      aria-label="Applied image tools"
      aria-orientation={isMobile ? "horizontal" : "vertical"}
      style={{
        ...workspaceStyles.toolRail,
        ...(isMobile ? workspaceStyles.mobileToolRail : {}),
      }}
    >
      {result.appliedFilters.map((toolId, index) => {
        const count =
          toolId === "cover-privates"
            ? privatePlacements.length
            : toolId === "hide-faces"
              ? result.faceCensorshipMode === "blur"
                ? faceBlurRegions.length
                : faceStickerPlacements.length
              : toolId === "blur-tattoos"
                ? getAutomaticRegionCount(result, activeImageIndex, "tattoo")
                : null;
        return (
          <ToolTab
            key={toolId}
            toolId={toolId}
            active={toolId === activeToolId}
            warning={warningToolIds.has(toolId)}
            count={count}
            mobile={isMobile}
            tabRef={(element) => {
              toolRefs.current[index] = element;
            }}
            onSelect={() => selectTool(toolId)}
            onKeyDown={(event) => handleToolKeyDown(event, index)}
          />
        );
      })}
    </div>
  );

  const inspector = (
    <section
      role="tabpanel"
      id={`image-tools-panel-${activeToolId}`}
      aria-labelledby={`image-tools-tab-${activeToolId}`}
      data-testid={`image-tools-inspector-${activeToolId}`}
      style={{
        ...workspaceStyles.inspector,
        ...(isMobile ? workspaceStyles.mobileInspector : {}),
      }}
    >
      <div style={workspaceStyles.inspectorHeadingRow}>
        <div style={workspaceStyles.inspectorHeadingCopy}>
          <h3 style={workspaceStyles.inspectorTitle}>
            {TOOL_DEFINITIONS[activeToolId].label}
          </h3>
          <InspectorStatus warning={warningToolIds.has(activeToolId)}>
            Applied
          </InspectorStatus>
        </div>
      </div>
      <p style={workspaceStyles.inspectorHelp}>
        {activeToolId === "hide-faces" && result.faceCensorshipMode === "blur"
          ? "Review every proposed region. Drag to move, select to resize, or use the arrow keys."
          : activeToolId === "hide-faces"
            ? "Select a face sticker on the canvas to move, resize, duplicate, or delete it."
            : activeToolId === "cover-privates"
              ? "Select a private cover on the canvas to move, resize, duplicate, or delete it."
              : "This filter has no manual controls in the current review editor."}
      </p>
      {renderInspectorContent()}
    </section>
  );

  const canvas = (
    <div
      ref={stageContainerRef}
      style={workspaceStyles.canvasColumn}
      data-testid={`unified-image-tools-canvas-${activeImageIndex}`}
    >
      <p
        id={`censorship-editor-help-${activeImageIndex}`}
        style={workspaceStyles.visuallyHidden}
      >
        Drag a sticker to move it. Select it to resize, use arrow keys, or
        duplicate it with Command or Control plus C. Delete or Backspace removes
        it.
      </p>
      <p
        id={`face-blur-editor-help-${activeImageIndex}`}
        style={workspaceStyles.visuallyHidden}
      >
        Drag to move, select to resize, or use arrow keys. Delete or Backspace
        removes the selected region.
      </p>
      <div
        data-testid={
          activeToolId === "hide-faces" && result.faceCensorshipMode === "blur"
            ? `face-blur-editor-${activeImageIndex}`
            : `censorship-editor-${activeImageIndex}`
        }
        style={{
          ...workspaceStyles.stage,
          ...(processedPreviewVisible &&
          result.appliedFilters.includes("remove-background")
            ? workspaceStyles.checkerboardStage
            : {}),
          width: stageSize.width || "100%",
          height:
            stageSize.height || imageToolsTokens.size.workspaceCanvasMinHeight,
        }}
        onClick={(event) => {
          if (event.currentTarget === event.target) selectLayer(null);
        }}
      >
        <img
          src={baseImageUrl}
          alt={`Photo ${activeImageIndex + 1} editing preview`}
          data-testid={
            result.faceCensorshipMode === "blur"
              ? `face-blur-source-image-${activeImageIndex}`
              : undefined
          }
          draggable={false}
          onLoad={(event) => {
            const nextSize = {
              width: event.currentTarget.naturalWidth,
              height: event.currentTarget.naturalHeight,
            };
            setImageSizes((current) =>
              current[activeImageIndex]?.width === nextSize.width &&
              current[activeImageIndex]?.height === nextSize.height
                ? current
                : {
                    ...current,
                    [activeImageIndex]: nextSize,
                  },
            );
          }}
          style={workspaceStyles.sourceImage}
        />
        {stageSize.width && stageSize.height && result.coverImageUrl
          ? privatePlacements.map((placement, index) => (
              <StickerOverlay
                key={`private-${placement.id}`}
                placement={placement}
                placementIndex={index}
                imageIndex={activeImageIndex}
                stageSize={stageSize}
                stickerUrl={result.coverImageUrl as string}
                selected={
                  activeToolId === "cover-privates" &&
                  placement.id === selectedLayerId
                }
                active={activeToolId === "cover-privates"}
                helpId={`censorship-editor-help-${activeImageIndex}`}
                onSelect={selectLayer}
                onChange={(id, update) => {
                  if (activeToolId === "cover-privates")
                    updateSticker(id, update);
                }}
                onStickerLoad={setStickerSize}
              />
            ))
          : null}
        {stageSize.width &&
        stageSize.height &&
        result.coverImageUrl &&
        result.faceCensorshipMode === "sticker"
          ? faceStickerPlacements.map((placement, index) => (
              <StickerOverlay
                key={`face-${placement.id}`}
                placement={placement}
                placementIndex={index}
                imageIndex={activeImageIndex}
                stageSize={stageSize}
                stickerUrl={result.coverImageUrl as string}
                selected={
                  activeToolId === "hide-faces" &&
                  placement.id === selectedLayerId
                }
                active={activeToolId === "hide-faces"}
                helpId={`censorship-editor-help-${activeImageIndex}`}
                onSelect={selectLayer}
                onChange={(id, update) => {
                  if (activeToolId === "hide-faces") updateSticker(id, update);
                }}
                onStickerLoad={setStickerSize}
              />
            ))
          : null}
        {stageSize.width &&
        stageSize.height &&
        result.faceCensorshipMode === "blur"
          ? faceBlurRegions.map((region, index) => (
              <FaceBlurOverlay
                key={region.id}
                region={region}
                regionIndex={index}
                imageIndex={activeImageIndex}
                stageSize={stageSize}
                selected={
                  activeToolId === "hide-faces" && region.id === selectedLayerId
                }
                active={activeToolId === "hide-faces"}
                showDebugLabels={showRegionDebugLabels}
                onSelect={selectLayer}
                onChange={updateFaceRegion}
              />
            ))
          : null}
        {!imageSize ? (
          <span aria-live="polite" style={workspaceStyles.canvasLoading}>
            Loading preview…
          </span>
        ) : null}
      </div>
    </div>
  );

  return (
    <section
      aria-labelledby="image-tools-review-heading"
      data-testid="image-tools-unified-editor"
      style={workspaceStyles.workspace}
    >
      <header style={workspaceStyles.workspaceHeader}>
        <div style={workspaceStyles.workspaceHeaderCopy}>
          <h2
            id="image-tools-review-heading"
            style={workspaceStyles.workspaceTitle}
          >
            Review edits
          </h2>
          <span style={workspaceStyles.photoPosition}>
            Photo {activeImageIndex + 1} of {result.downloadUrls.length}
          </span>
        </div>
        <SecondaryButton
          text="Download All"
          onPress={onDownloadAll}
          loading={isDownloadingAll}
          disabled={isDownloadingAll || isHighQualityExporting || !editorsReady}
          fullWidth={isMobile}
          icon={
            <ImageFilterIcon name="download" color={tokens.color.textPrimary} />
          }
        />
      </header>

      {isTablet ? photoNavigator : null}

      {isMobile ? (
        <div style={workspaceStyles.mobileWorkspaceBody}>
          {canvas}
          {toolRail}
          {inspector}
        </div>
      ) : (
        <div style={workspaceStyles.workspaceBody}>
          {!isTablet ? photoNavigator : null}
          {canvas}
          <div style={workspaceStyles.rightPanel}>
            {inspector}
            {toolRail}
          </div>
        </div>
      )}

      <footer
        style={{
          ...workspaceStyles.workspaceFooter,
          ...(isMobile ? workspaceStyles.mobileWorkspaceFooter : {}),
        }}
      >
        <div style={workspaceStyles.footerCopy}>
          <strong style={workspaceStyles.footerTitle}>
            Photo {activeImageIndex + 1}
          </strong>
          <span style={workspaceStyles.footerSummary}>
            {summaryParts.join(" · ")}
          </span>
          {currentPhotoDownloadDisabled &&
          result.appliedFilters.includes("hide-faces") &&
          (result.faceCensorshipMode === "blur"
            ? faceBlurRegions.length === 0
            : faceStickerPlacements.length === 0) ? (
            <span role="status" style={workspaceStyles.downloadReason}>
              Add at least one approved face{" "}
              {result.faceCensorshipMode === "blur" ? "region" : "sticker"}{" "}
              before export.
            </span>
          ) : null}
        </div>
        <SecondaryButton
          text="Download Photo"
          onPress={() => onDownload(activeImageIndex)}
          loading={downloadingIndex === activeImageIndex}
          disabled={currentPhotoDownloadDisabled}
          fullWidth={isMobile}
          icon={
            <ImageFilterIcon name="download" color={tokens.color.textPrimary} />
          }
        />
      </footer>
    </section>
  );
};

const workspaceStyles: Record<string, React.CSSProperties> = {
  workspace: {
    width: "100%",
    overflow: "hidden",
    border: `${tokens.borderWidth.thin}px solid ${tokens.color.border}`,
    borderRadius: tokens.radius.lg,
    backgroundColor: tokens.color.surface,
  },
  workspaceHeader: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacing.md,
    padding: tokens.spacing.lg,
    borderBottom: `${tokens.borderWidth.thin}px solid ${tokens.color.border}`,
  },
  workspaceHeaderCopy: {
    minWidth: 0,
    display: "flex",
    flexWrap: "wrap",
    alignItems: "baseline",
    gap: tokens.spacing.md,
  },
  workspaceTitle: {
    margin: 0,
    color: tokens.color.textPrimary,
    fontFamily: tokens.font.bold,
    fontSize: tokens.fontSize.xl,
    lineHeight: `${tokens.lineHeight.xl}px`,
  },
  photoPosition: {
    color: tokens.color.textSecondary,
    fontFamily: tokens.font.medium,
    fontSize: tokens.fontSize.sm,
    lineHeight: `${tokens.lineHeight.sm}px`,
  },
  workspaceBody: {
    minWidth: 0,
    display: "flex",
    alignItems: "stretch",
    backgroundColor: tokens.color.surfaceMuted,
  },
  mobileWorkspaceBody: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: tokens.color.surfaceMuted,
  },
  canvasColumn: {
    minWidth: 0,
    minHeight: imageToolsTokens.size.workspaceCanvasMinHeight,
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacing.lg,
    boxSizing: "border-box",
  },
  stage: {
    position: "relative",
    overflow: "hidden",
    maxWidth: "100%",
    borderRadius: tokens.radius.lg,
    backgroundColor: tokens.color.surfaceMuted,
    touchAction: "none",
  },
  checkerboardStage: {
    backgroundColor: tokens.color.surface,
    backgroundImage: `linear-gradient(45deg, ${tokens.color.surfaceMuted} 25%, transparent 25%), linear-gradient(-45deg, ${tokens.color.surfaceMuted} 25%, transparent 25%), linear-gradient(45deg, transparent 75%, ${tokens.color.surfaceMuted} 75%), linear-gradient(-45deg, transparent 75%, ${tokens.color.surfaceMuted} 75%)`,
    backgroundSize: `${tokens.spacing.lg}px ${tokens.spacing.lg}px`,
    backgroundPosition: `0 0, 0 ${tokens.spacing.sm}px, ${tokens.spacing.sm}px -${tokens.spacing.sm}px, -${tokens.spacing.sm}px 0`,
  },
  sourceImage: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "fill",
    pointerEvents: "none",
    userSelect: "none",
  },
  canvasLoading: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: tokens.color.textSecondary,
    fontFamily: tokens.font.medium,
    fontSize: tokens.fontSize.sm,
    lineHeight: `${tokens.lineHeight.sm}px`,
    backgroundColor: tokens.color.surfaceMuted,
  },
  sticker: {
    cursor: "move",
    zIndex: tokens.zIndex.base,
  },
  region: {
    cursor: "move",
    zIndex: tokens.zIndex.base,
  },
  interactiveLayer: {
    pointerEvents: "auto",
  },
  inactiveLayer: {
    pointerEvents: "none",
  },
  selectedSticker: {
    zIndex: tokens.zIndex.above,
    outline: `${tokens.borderWidth.strong}px dashed ${tokens.color.accent}`,
    outlineOffset: tokens.borderWidth.strong,
    backgroundColor: tokens.color.surfaceAccent,
  },
  layerFocusTarget: {
    position: "relative",
    width: "100%",
    height: "100%",
    outline: "none",
  },
  stickerImage: {
    width: "100%",
    height: "100%",
    display: "block",
    objectFit: "fill",
    pointerEvents: "none",
    userSelect: "none",
  },
  blurPreview: {
    position: "absolute",
    inset: 0,
    backdropFilter: `blur(${tokens.spacing.sm}px)`,
  },
  regionPolygon: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    overflow: "visible",
    pointerEvents: "none",
  },
  regionBadge: {
    position: "absolute",
    top: tokens.spacing.xs,
    left: tokens.spacing.xs,
    maxWidth: `calc(100% - ${tokens.spacing.sm}px)`,
    padding: `${tokens.spacing.xs}px ${tokens.spacing.sm}px`,
    borderRadius: tokens.radius.round,
    backgroundColor: tokens.color.overlay,
    color: tokens.color.inverse,
    fontFamily: tokens.font.semibold,
    fontSize: tokens.fontSize.xs,
    lineHeight: `${tokens.lineHeight.xs}px`,
    pointerEvents: "none",
  },
  rightPanel: {
    minWidth: 0,
    display: "flex",
    alignItems: "stretch",
    borderLeft: `${tokens.borderWidth.thin}px solid ${tokens.color.border}`,
    backgroundColor: tokens.color.surface,
  },
  inspector: {
    width: imageToolsTokens.size.workspaceInspectorWidth,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing.md,
    padding: tokens.spacing.lg,
    boxSizing: "border-box",
  },
  mobileInspector: {
    width: "100%",
    borderTop: `${tokens.borderWidth.thin}px solid ${tokens.color.border}`,
    backgroundColor: tokens.color.surface,
  },
  inspectorHeadingRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: tokens.spacing.sm,
  },
  inspectorHeadingCopy: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: tokens.spacing.xs,
  },
  inspectorTitle: {
    margin: 0,
    color: tokens.color.textPrimary,
    fontFamily: tokens.font.bold,
    fontSize: tokens.fontSize.lg,
    lineHeight: `${tokens.lineHeight.lg}px`,
  },
  inspectorStatus: {
    padding: `${tokens.spacing.xs}px ${tokens.spacing.sm}px`,
    border: `${tokens.borderWidth.thin}px solid ${tokens.color.successBorder}`,
    borderRadius: tokens.radius.round,
    color: tokens.color.successText,
    backgroundColor: tokens.color.successSurface,
    fontFamily: tokens.font.semibold,
    fontSize: tokens.fontSize.xs,
    lineHeight: `${tokens.lineHeight.xs}px`,
  },
  inspectorWarningStatus: {
    borderColor: tokens.color.errorBorder,
    color: tokens.color.errorText,
    backgroundColor: tokens.color.errorSurface,
  },
  inspectorHelp: {
    margin: 0,
    color: tokens.color.textSecondary,
    fontFamily: tokens.font.regular,
    fontSize: tokens.fontSize.sm,
    lineHeight: `${tokens.lineHeight.sm}px`,
  },
  inspectorMuted: {
    display: "block",
    margin: 0,
    color: tokens.color.textSecondary,
    fontFamily: tokens.font.regular,
    fontSize: tokens.fontSize.xs,
    lineHeight: `${tokens.lineHeight.xs}px`,
  },
  inspectorStrong: {
    color: tokens.color.textPrimary,
    fontFamily: tokens.font.semibold,
  },
  modeRow: {
    minHeight: imageToolsTokens.size.iconButton,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacing.sm,
    padding: tokens.spacing.md,
    borderRadius: tokens.radius.md,
    backgroundColor: tokens.color.surfaceMuted,
  },
  modeLabel: {
    color: tokens.color.textSecondary,
    fontFamily: tokens.font.medium,
    fontSize: tokens.fontSize.sm,
    lineHeight: `${tokens.lineHeight.sm}px`,
  },
  modeValue: {
    color: tokens.color.textPrimary,
    fontFamily: tokens.font.semibold,
    fontSize: tokens.fontSize.sm,
    lineHeight: `${tokens.lineHeight.sm}px`,
  },
  stickerPreviewRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacing.md,
  },
  stickerPreviewBox: {
    width: imageToolsTokens.size.workspaceThumbnail,
    height: imageToolsTokens.size.workspaceThumbnail,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacing.sm,
    boxSizing: "border-box",
    border: `${tokens.borderWidth.thin}px solid ${tokens.color.border}`,
    borderRadius: tokens.radius.md,
    backgroundColor: tokens.color.surfaceMuted,
  },
  stickerPreviewImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  stickerCopy: {
    minWidth: 0,
    display: "flex",
    flex: 1,
    flexDirection: "column",
    gap: tokens.spacing.xs,
  },
  selectionCopy: {
    margin: 0,
    color: tokens.color.textSecondary,
    fontFamily: tokens.font.medium,
    fontSize: tokens.fontSize.xs,
    lineHeight: `${tokens.lineHeight.xs}px`,
  },
  inspectorActions: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: tokens.spacing.sm,
  },
  notice: {
    padding: tokens.spacing.md,
    border: `${tokens.borderWidth.thin}px solid ${tokens.color.accentMuted}`,
    borderRadius: tokens.radius.md,
    color: tokens.color.textPrimary,
    backgroundColor: tokens.color.surfaceAccent,
    fontFamily: tokens.font.regular,
    fontSize: tokens.fontSize.xs,
    lineHeight: `${tokens.lineHeight.xs}px`,
  },
  errorNotice: {
    padding: tokens.spacing.md,
    border: `${tokens.borderWidth.thin}px solid ${tokens.color.errorBorder}`,
    borderRadius: tokens.radius.md,
    color: tokens.color.errorText,
    backgroundColor: tokens.color.errorSurface,
    fontFamily: tokens.font.medium,
    fontSize: tokens.fontSize.xs,
    lineHeight: `${tokens.lineHeight.xs}px`,
  },
  appliedPanel: {
    minHeight: imageToolsTokens.size.iconButton,
    display: "flex",
    alignItems: "center",
    gap: tokens.spacing.md,
    padding: tokens.spacing.md,
    borderRadius: tokens.radius.md,
    backgroundColor: tokens.color.successSurface,
  },
  appliedMark: {
    width: tokens.spacing.xxxl,
    height: tokens.spacing.xxxl,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    borderRadius: tokens.radius.round,
    color: tokens.color.successText,
    backgroundColor: tokens.color.surface,
    fontFamily: tokens.font.bold,
    fontSize: tokens.fontSize.md,
    lineHeight: `${tokens.lineHeight.md}px`,
  },
  toolRail: {
    width: imageToolsTokens.size.workspaceRailWidth,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: tokens.spacing.sm,
    padding: tokens.spacing.sm,
    boxSizing: "border-box",
    borderLeft: `${tokens.borderWidth.thin}px solid ${tokens.color.border}`,
    backgroundColor: tokens.color.surfaceMuted,
  },
  mobileToolRail: {
    width: "100%",
    flexDirection: "row",
    overflowX: "auto",
    borderTop: `${tokens.borderWidth.thin}px solid ${tokens.color.border}`,
    borderLeft: "none",
  },
  toolTab: {
    minWidth: imageToolsTokens.size.iconButton,
    minHeight: imageToolsTokens.size.iconButton,
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacing.xs,
    padding: tokens.spacing.xs,
    border: `${tokens.borderWidth.thin}px solid ${tokens.color.border}`,
    borderRadius: tokens.radius.md,
    color: tokens.color.textPrimary,
    backgroundColor: tokens.color.surface,
    cursor: "pointer",
  },
  mobileToolTab: {
    minWidth: imageToolsTokens.size.filterOptionMobile,
    flexShrink: 0,
  },
  activeToolTab: {
    borderColor: tokens.color.accent,
    backgroundColor: tokens.color.surfaceAccent,
  },
  hoveredControl: {
    borderColor: tokens.color.accentDark,
  },
  focusedControl: {
    outline: `${tokens.borderWidth.strong}px solid ${tokens.color.accentDark}`,
    outlineOffset: tokens.borderWidth.strong,
  },
  pressedControl: {
    transform: `scale(${imageToolsTokens.motion.pressScale})`,
  },
  toolIconWrap: {
    position: "relative",
    display: "inline-flex",
  },
  toolStatus: {
    position: "absolute",
    top: -tokens.spacing.sm,
    right: -tokens.spacing.sm,
    minWidth: tokens.spacing.xl,
    height: tokens.spacing.xl,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: `0 ${tokens.spacing.xs}px`,
    boxSizing: "border-box",
    border: `${tokens.borderWidth.thin}px solid ${tokens.color.successBorder}`,
    borderRadius: tokens.radius.round,
    color: tokens.color.successText,
    backgroundColor: tokens.color.successSurface,
    fontFamily: tokens.font.bold,
    fontSize: tokens.fontSize.xs,
    lineHeight: `${tokens.lineHeight.xs}px`,
  },
  warningStatus: {
    borderColor: tokens.color.errorBorder,
    color: tokens.color.errorText,
    backgroundColor: tokens.color.errorSurface,
  },
  toolTabLabel: {
    width: "100%",
    overflow: "hidden",
    color: tokens.color.textPrimary,
    fontFamily: tokens.font.medium,
    fontSize: tokens.fontSize.xs,
    lineHeight: `${tokens.lineHeight.xs}px`,
    textAlign: "center",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  photoNavigator: {
    width: imageToolsTokens.size.workspaceNavigatorWidth,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing.sm,
    padding: tokens.spacing.md,
    boxSizing: "border-box",
    overflowY: "auto",
    borderRight: `${tokens.borderWidth.thin}px solid ${tokens.color.border}`,
    backgroundColor: tokens.color.surface,
  },
  horizontalPhotoNavigator: {
    width: "100%",
    flexDirection: "row",
    overflowX: "auto",
    overflowY: "hidden",
    borderRight: "none",
    borderBottom: `${tokens.borderWidth.thin}px solid ${tokens.color.border}`,
  },
  photoButton: {
    position: "relative",
    width: imageToolsTokens.size.workspaceThumbnail,
    minWidth: imageToolsTokens.size.workspaceThumbnail,
    minHeight: imageToolsTokens.size.workspaceThumbnail,
    overflow: "hidden",
    padding: 0,
    border: `${tokens.borderWidth.strong}px solid ${tokens.color.border}`,
    borderRadius: tokens.radius.md,
    backgroundColor: tokens.color.surfaceMuted,
    cursor: "pointer",
  },
  activePhotoButton: {
    borderColor: tokens.color.accent,
  },
  photoThumbnail: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  photoNumber: {
    position: "absolute",
    left: tokens.spacing.xs,
    bottom: tokens.spacing.xs,
    minWidth: tokens.spacing.xxl,
    height: tokens.spacing.xxl,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: tokens.radius.round,
    color: tokens.color.inverse,
    backgroundColor: tokens.color.overlay,
    fontFamily: tokens.font.bold,
    fontSize: tokens.fontSize.xs,
    lineHeight: `${tokens.lineHeight.xs}px`,
  },
  photoWarning: {
    position: "absolute",
    top: tokens.spacing.xs,
    right: tokens.spacing.xs,
    width: tokens.spacing.xxl,
    height: tokens.spacing.xxl,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: tokens.radius.round,
    color: tokens.color.errorText,
    backgroundColor: tokens.color.errorSurface,
    fontFamily: tokens.font.bold,
    fontSize: tokens.fontSize.xs,
    lineHeight: `${tokens.lineHeight.xs}px`,
  },
  workspaceFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacing.md,
    padding: tokens.spacing.lg,
    borderTop: `${tokens.borderWidth.thin}px solid ${tokens.color.border}`,
  },
  mobileWorkspaceFooter: {
    flexDirection: "column",
    alignItems: "stretch",
  },
  footerCopy: {
    minWidth: 0,
    display: "flex",
    flex: 1,
    flexDirection: "column",
    gap: tokens.spacing.xs,
  },
  footerTitle: {
    color: tokens.color.textPrimary,
    fontFamily: tokens.font.semibold,
    fontSize: tokens.fontSize.sm,
    lineHeight: `${tokens.lineHeight.sm}px`,
  },
  footerSummary: {
    color: tokens.color.textMuted,
    fontFamily: tokens.font.regular,
    fontSize: tokens.fontSize.xs,
    lineHeight: `${tokens.lineHeight.xs}px`,
  },
  downloadReason: {
    color: tokens.color.errorText,
    fontFamily: tokens.font.medium,
    fontSize: tokens.fontSize.xs,
    lineHeight: `${tokens.lineHeight.xs}px`,
  },
  visuallyHidden: {
    position: "absolute",
    width: tokens.borderWidth.thin,
    height: tokens.borderWidth.thin,
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
  },
};

export default UnifiedImageToolsEditor;
